import React, { useContext } from 'react';
import styled from '@emotion/styled';
import FalconContext from '../contexts/falcon.context';
import FirstStage from './first-stage';
import InterStage from './inter-stage';
import SecondStage from './second-stage';
import Payload from './payload';
import FirstStageSmoke from './first-stage-smoke';
import FirstStageFlare from './first-stage-flare';
import SecondStageFlare from './second-stage-flare';
import Tower from './tower';
import Launchpad from './launchpad';
import ISS from './iss';
import Smoke from './smoke';
import {
  moveUp,
  shakeLittle,
  dropFirstStage,
  dropInterStage,
  flicker,
  moveDown,
  dropSecondStage,
  towerLean,
  moveClouds,
  tilt,
  space,
  flashMove,
  launchPadMove,
  moveIn,
} from '../styles/animations';
import { Words, Content, withStyles, Frame } from 'arwes';

const styles = theme => {
  return {
    finalText: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      fontSize: 60,
      textTransform: 'uppercase',
      transform: 'translate(-50%, -50%)',
    },
  };
};

const StyledFalconWrapper = styled.div`
  &.tilt {
    animation: ${tilt} 60s forwards;
    animation-delay: 6s;
  }
`;

const StyledFalcon = styled.div`
  position: relative;
  height: calc(100vh - 90px);
  display: inline-block;

  &.liftoff {
    animation: ${moveUp} 3s forwards;
  }

  &.shake {
    animation: ${shakeLittle} 0.5s infinite;
  }
`;

const StyledFirstStage = styled(FirstStage)`
  position: absolute;
  z-index: 5;
  bottom: 0px;
  height: 330px;

  &.drop {
    animation: ${dropFirstStage} 4s forwards;
  }
`;

const StyledInterStage = styled(InterStage)`
  position: absolute;
  z-index: 10;
  bottom: 324px;
  left: -2px;
  height: 78px;

  &.drop {
    animation: ${dropInterStage} 4s forwards;
  }
`;

const StyledSecondStage = styled(SecondStage)`
  position: absolute;
  z-index: 4;
  bottom: 349px;
  left: 1px;
  height: 130px;

  &.drop {
    animation: ${dropSecondStage} 4s forwards;
  }
`;

const StyledPayload = styled(Payload)`
  position: absolute;
  z-index: 15;
  bottom: 478px;
  left: 0px;
  height: 85px;

  &.flash {
    animation: ${flashMove} 4s forwards ease-in;
  }
`;

const StyledRightFirstStageSmoke = styled(FirstStageSmoke)`
  position: absolute;
  bottom: 4px;
  left: -360px;
  bottom: -50px;
  transform: rotateY(180deg);

  &.flare-off {
    animation: ${flicker} 2s forwards;
  }
`;

const StyledLeftFirstStageSmoke = styled(FirstStageSmoke)`
  position: absolute;
  bottom: -50px;
  left: -300px;
  z-index: 700;
  &.flare-off {
    animation: ${flicker} 2s forwards;
  }
`;

const StyledFirstStageFlare = styled(FirstStageFlare)`
  position: absolute;
  bottom: -40px;
  height: 180px;
  width: auto;
  left: 12px;
  mask-image: ${props =>
    props.throttleDown
      ? `radial-gradient(ellipse 100% 100% at 50% 0%, black 40%, transparent 50%)`
      : `radial-gradient(ellipse 200% 200% at 50% 0%, black 40%, transparent 50%)`};

  &.flare-off {
    animation: ${flicker} 2s forwards;
  }
  .liftoff & {
    animation: ${moveDown} 2s forwards;
  }
`;

const StyledSecondStageFlare = styled(SecondStageFlare)`
  position: absolute;
  bottom: 235px;
  height: 120px;
  width: auto;
  left: 17px;
  opacity: 0;
  mask-image: radial-gradient(ellipse 200% 200% at 50% 0%, black 40%, transparent 50%);

  animation: ${flicker} 2s reverse forwards;

  &.flare-off {
    animation: ${flicker} 2s;
  }
`;

const StyledTower = styled(Tower)`
  position: absolute;
  left: 455px;
  bottom: 5px;
  height: 520px;
  transform-origin: bottom;
  transform: rotate(0deg);

  &.liftoff {
    animation: ${towerLean} 4s forwards;
  }
`;

const StyledLaunchpad = styled(Launchpad)`
  position: absolute;
  left: 20px;
  bottom: 5px;
  height: 650px;
  transform-origin: bottom;
  z-index: 6;

  &.liftoff {
    animation: ${launchPadMove} 4s forwards;
  }
`;
const StyledCloudsBackground = styled.div`
  background: url('assets/clouds_repeat.png') repeat;
  width: 2500px;
  height: 16000px;
  position: absolute;
  top: 0;
  left: 0;

  &.liftoff {
    animation: ${moveClouds} 20s linear infinite;
  }
`;

const StyledSpaceBackground = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 1500px;
  height: 1000px;
  background: black;
  opacity: 0;

  &.space {
    animation: ${space} 15s ease-in-out forwards;
  }
`;

const StyledISS = styled(ISS)`
  position: absolute;
  z-index: 20;
  top: 0;
  left: -50px;
  @media (min-width: 1600px) {
    top: 30px;
    left: -100px;
  }

  animation: ${moveIn} 15s ease-in-out forwards;
`;

const StyledSmoke = styled(Smoke)``; // Canvas styles are not working with emotion

const AssembledFalcon = props => {
  const { classes } = props;
  const falconState = useContext(FalconContext);
  const shakeStates = ['engineIgnition', 'countDownTMinus1'];
  const postCountDownStates = [
    'liftoff',
    'preMaxQ',
    'maxQ',
    'superSonic',
    'postSuperSonic',
    'preMeco',
    'meco',
    'firstStageDropOff',
    'stagesSeparate',
    'ses',
    'seco',
    'secondStageDropOff',
    'gnomesDeploy',
    'end',
  ];
  const firstStageDropStates = [
    'firstStageDropOff',
    'stagesSeparate',
    'ses',
    'seco',
    'secondStageDropOff',
    'gnomesDeploy',
    'end',
  ];
  const interStageDropsStates = [
    'stagesSeparate',
    'ses',
    'seco',
    'secondStageDropOff',
    'gnomesDeploy',
    'end',
  ];

  const issStates = ['gnomesDeploy', 'end'];
  const secondStageDropsStates = ['secondStageDropOff', 'gnomesDeploy', 'end'];
  const throttleDownStates = ['preMaxQ', 'maxQ', 'superSonic', 'preMeco'];
  const flashMoveStates = ['gnomesDeploy', 'end'];

  const shakeClassName = shakeStates.includes(falconState.state) ? 'shake' : '';
  const liftOffClassName = postCountDownStates.includes(falconState.state) ? 'liftoff' : '';
  const tiltClassName = postCountDownStates.includes(falconState.state) ? 'tilt' : '';
  const firstStageDropClassName = firstStageDropStates.includes(falconState.state) ? 'drop' : '';
  const spaceClassName = firstStageDropStates.includes(falconState.state) ? 'space' : '';
  const interStageDropClassName = interStageDropsStates.includes(falconState.state) ? 'drop' : '';
  const secondStageDropClassName = secondStageDropsStates.includes(falconState.state) ? 'drop' : '';
  const flashMoveClassName = flashMoveStates.includes(falconState.state) ? 'flash' : '';
  const firstStageFlareOffClassName = falconState.state === 'meco' ? 'flare-off' : '';
  const secondStageFlareOffClassName = falconState.state === 'seco' ? 'flare-off' : '';
  const throttleDown = throttleDownStates.includes(falconState.state);

  return (
    <>
      <StyledCloudsBackground className={`${liftOffClassName} ${spaceClassName}`} />
      <StyledSpaceBackground className={`${spaceClassName}`} />
      <StyledTower className={`${liftOffClassName}`} />
      <StyledLaunchpad className={`${liftOffClassName}`} />
      {/* <StyledSmoke /> */}
      <StyledFalconWrapper className={`${tiltClassName}`}>
        <StyledFalcon className={`${shakeClassName} ${liftOffClassName} ${tiltClassName}`}>
          <StyledFirstStage className={`${firstStageDropClassName}`} />
          <StyledInterStage className={`${interStageDropClassName}`} />
          <StyledSecondStage className={`${secondStageDropClassName}`} />
          <StyledSecondStageFlare className={`${secondStageFlareOffClassName}`} />
          <StyledPayload className={`${flashMoveClassName}`} />
          <>
            <StyledRightFirstStageSmoke />
            <StyledLeftFirstStageSmoke />
          </>
          <StyledFirstStageFlare
            throttleDown={throttleDown}
            className={`${firstStageFlareOffClassName}`}
          />
        </StyledFalcon>
      </StyledFalconWrapper>
      {issStates.includes(falconState.state) ? <StyledISS /> : null}
    </>
  );
};

export default withStyles(styles)(AssembledFalcon);
